import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import { Transition } from '../../components/Transition';
import { NumericFormatCustom } from '../../components/NumericCustomFormatter';

interface IProps {
  toggle: boolean;
  onClose: () => void;
  values: any;
  handleSubmit: () => void;
  setFieldValue: (key: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<any>) => void;
  errors: any;
  touched: any;
  isSubmitting: boolean;
  resetForm: () => void;
}

const BundleCategoryFormModal: React.FC<IProps> = ({
  toggle,
  onClose,
  errors,
  touched,
  values,
  isSubmitting,
  setFieldValue,
  handleChange,
  handleSubmit,
  resetForm,
}) => {
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const [isPickerOpen, setIsPickerOpen] = useState<boolean>(false);
  const isEdit = Boolean(values?.id);

  const toggleEmojiPicker = () => {
    setIsPickerOpen((prev) => !prev);
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setFieldValue('emoji', emojiData?.emoji);
    setIsPickerOpen((prev) => !prev);
  };

  return (
    <Dialog
      fullWidth
      open={toggle}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => {
        resetForm();
        setIsPickerOpen(false);
        onClose();
      }}
      aria-describedby='bundle-category-dialog-description'
    >
      <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>
        {isEdit ? 'Edit Bundle Category' : 'New Bundle Category'}
      </DialogTitle>
      <form
        onSubmit={handleSubmit}
        autoComplete='off'
      >
        <DialogContent
          dividers
          sx={{
            overflow: isPickerOpen ? 'visible' : 'auto',
          }}
        >
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                id='labelEn'
                name='labelEn'
                label='Label English'
                placeholder='Category name'
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.labelEn}
                error={Boolean(errors.labelEn && touched.labelEn)}
                helperText={
                  Boolean(errors.labelEn && touched.labelEn) && errors.labelEn
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                id='labelAr'
                name='labelAr'
                label='Label Arabic'
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.labelAr}
                error={Boolean(errors.labelAr && touched.labelAr)}
                helperText={
                  Boolean(errors.labelAr && touched.labelAr) && errors.labelAr
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
              sx={{
                position: 'relative',
                zIndex: isPickerOpen ? 20 : 'auto',
              }}
            >
              <TextField
                id='emoji'
                name='emoji'
                label={'Emoji'}
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.emoji}
                error={Boolean(errors.emoji && touched.emoji)}
                helperText={
                  Boolean(errors.emoji && touched.emoji) && errors.emoji
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton onClick={toggleEmojiPicker}>
                        <InsertEmoticonIcon />
                      </IconButton>
                      {isPickerOpen && (
                        <Box
                          position='absolute'
                          zIndex={30}
                          right={'50px'}
                          top={'-275%'}
                          sx={{
                            width: isXsScreen ? 200 : 350,
                            maxWidth: isXsScreen ? 200 : 350,
                            overflow: 'hidden',
                            '& .EmojiPickerReact': {
                              width: '100% !important',
                            },
                            '& .epr-preview': {
                              overflow: 'hidden',
                            },
                            '& .epr-preview-emoji-label, & .epr-preview-name': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                            },
                          }}
                        >
                          <EmojiPicker
                            onEmojiClick={handleEmojiClick}
                            skinTonesDisabled={true}
                            width={isXsScreen ? 200 : 350}
                            height={350}
                          />
                        </Box>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <TextField
                id='position'
                name='position'
                label='Position'
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.position}
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                }}
                error={Boolean(errors.position && touched.position)}
                helperText={
                  Boolean(errors.position && touched.position) &&
                  errors.position
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              <FormControl fullWidth>
                <InputLabel id='bundle-category-status-label'>Status</InputLabel>
                <Select
                  labelId='bundle-category-status-label'
                  id='activeState'
                  name='activeState'
                  label='Status'
                  value={values.activeState ? 'active' : 'inactive'}
                  onChange={(e) =>
                    setFieldValue('activeState', e.target.value === 'active')
                  }
                >
                  <MenuItem value='active'>Active</MenuItem>
                  <MenuItem value='inactive'>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    color='primary'
                    name='showOnHomePage'
                    checked={Boolean(values.showOnHomePage)}
                    onChange={(e) =>
                      setFieldValue('showOnHomePage', e.target.checked)
                    }
                  />
                }
                label='Show on Home Page'
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              resetForm();
              setIsPickerOpen(false);
              onClose();
            }}
            variant='outlined'
          >
            Cancel
          </Button>
          <LoadingButton
            type='submit'
            loading={isSubmitting}
            variant='contained'
          >
            {isEdit ? 'Update Category' : 'Create Category'}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BundleCategoryFormModal;
